// src/components/mensagens/StatsCards.jsx
// ============================================================================
// 🎯 COMPONENTE: StatsCards
// 📋 Função: Exibe cards de estatísticas de mensagens
// 📱 Responsivo: Adaptado para mobile e desktop
// ============================================================================

import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Phone, 
  MessageSquare, 
  TrendingUp, 
  History,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { getStatsCardColors } from './helpers';

/**
 * 🎯 Componente de cards de estatísticas
 * @param {Object} props
 * @param {Object} props.stats - Estatísticas básicas (total, comWhatsApp, mensagensHoje, etc)
 * @param {Object} props.advancedStats - Estatísticas avançadas (taxaLeitura, taxaResposta)
 * @param {string} props.primaryColor - Cor primária do tema
 * @param {boolean} props.compact - Versão compacta (opcional)
 */
export const StatsCards = ({ 
  stats, 
  advancedStats,
  primaryColor = '#10b981',
  compact = false 
}) => {
  const isMobile = useIsMobile();

  // Configuração dos cards
  const cards = [
    { 
      key: 'total',
      label: 'Total', 
      value: stats.total, 
      color: 'blue', 
      icon: User,
      tooltip: 'Total de clientes cadastrados'
    },
    { 
      key: 'whatsapp',
      label: 'WhatsApp', 
      value: stats.comWhatsApp, 
      color: 'green', 
      icon: Phone,
      tooltip: 'Clientes com WhatsApp válido'
    },
    { 
      key: 'hoje',
      label: 'Hoje', 
      value: stats.mensagensHoje, 
      color: 'purple', 
      icon: MessageSquare,
      tooltip: 'Mensagens enviadas hoje'
    },
    { 
      key: 'leitura',
      label: 'Leitura', 
      value: `${advancedStats?.taxaLeitura || 0}%`, 
      color: 'indigo', 
      icon: TrendingUp,
      tooltip: 'Taxa de leitura das mensagens'
    },
    { 
      key: 'contactados',
      label: 'Contactados', 
      value: stats.clientesContactados, 
      color: 'amber', 
      icon: History,
      tooltip: 'Clientes contactados no período'
    }
  ];

  // Versão compacta (apenas 3 cards principais)
  const compactCards = compact ? cards.slice(0, 3) : cards;

  // Grid layout baseado no modo
  const gridClass = compact || isMobile
    ? 'grid-cols-2 gap-2'
    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4';

  // Animação de entrada
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className={`grid ${gridClass}`}
    >
      {compactCards.map((card) => {
        const Icon = card.icon;
        const colors = getStatsCardColors(card.color);

        return (
          <motion.div
            key={card.key}
            variants={itemVariants}
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 group relative"
          >
            {/* Tooltip - apenas desktop */}
            {!isMobile && card.tooltip && (
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {card.tooltip}
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            )}

            <div className="flex items-center gap-2">
              {/* Ícone com fundo colorido */}
              <div className={`p-1.5 ${colors.bg} rounded-lg flex-shrink-0`}>
                <Icon size={isMobile ? 16 : 20} className={colors.icon} />
              </div>

              {/* Valores */}
              <div className="min-w-0 flex-1">
                <p className={`text-gray-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                  {card.label}
                </p>
                <div className="flex items-center gap-1">
                  <p className={`font-bold text-gray-800 truncate ${
                    isMobile ? 'text-base' : 'text-xl md:text-2xl'
                  }`}>
                    {card.value}
                  </p>
                  
                  {/* Badge adicional para taxa de leitura */}
                  {card.key === 'leitura' && advancedStats?.taxaResposta > 0 && !compact && (
                    <span 
                      className="text-[8px] px-1 py-0.5 rounded-full bg-green-100 text-green-700"
                      title={`${advancedStats.taxaResposta}% de resposta`}
                    >
                      {advancedStats.taxaResposta}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Barra de progresso para alguns cards (opcional) */}
            {!compact && card.key === 'leitura' && (
              <div className="mt-2 w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${advancedStats?.taxaLeitura || 0}%` }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: primaryColor }}
                />
              </div>
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
};

/**
 * 🎯 Componente de estatísticas detalhadas (versão expandida)
 * @param {Object} props
 * @param {Array} props.messages - Lista de mensagens
 * @param {Object} props.stats - Estatísticas
 */
export const DetailedStats = ({ messages = [], stats }) => {
  const isMobile = useIsMobile();

  // Calcular estatísticas adicionais
  const totalMensagens = messages.length;
  const lidas = messages.filter(m => m.status === 'read').length;
  const respondidas = messages.filter(m => m.direction === 'incoming').length;
  const falhas = messages.filter(m => m.status === 'failed').length;

  const metrics = [
    {
      label: 'Total mensagens',
      value: totalMensagens,
      icon: MessageSquare,
      color: 'blue'
    },
    {
      label: 'Lidas',
      value: lidas,
      icon: CheckCircle,
      color: 'green'
    },
    {
      label: 'Respondidas',
      value: respondidas,
      icon: History,
      color: 'purple'
    },
    {
      label: 'Falhas',
      value: falhas,
      icon: AlertCircle,
      color: 'red'
    }
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
        <TrendingUp size={18} className="text-gray-500" />
        Métricas detalhadas
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const colorClasses = {
            blue: 'text-blue-600 bg-blue-50',
            green: 'text-green-600 bg-green-50',
            purple: 'text-purple-600 bg-purple-50',
            red: 'text-red-600 bg-red-50'
          };

          return (
            <div key={index} className="p-3 rounded-lg border border-gray-100">
              <div className="flex items-center gap-2 mb-1">
                <div className={`p-1 rounded-lg ${colorClasses[metric.color]}`}>
                  <Icon size={isMobile ? 14 : 16} />
                </div>
                <span className={`text-gray-500 ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                  {metric.label}
                </span>
              </div>
              <p className={`font-bold text-gray-900 ${isMobile ? 'text-base' : 'text-lg'}`}>
                {metric.value}
              </p>
              {metric.label === 'Lidas' && totalMensagens > 0 && (
                <p className="text-[10px] text-gray-400">
                  {Math.round((metric.value / totalMensagens) * 100)}% do total
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Gráfico de pizza simplificado (opcional) */}
      {totalMensagens > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Distribuição de status</span>
            <span className="text-xs text-gray-400">{totalMensagens} total</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden">
            {lidas > 0 && (
              <div 
                className="bg-green-500 h-full"
                style={{ width: `${(lidas / totalMensagens) * 100}%` }}
              />
            )}
            {respondidas > 0 && (
              <div 
                className="bg-blue-500 h-full"
                style={{ width: `${(respondidas / totalMensagens) * 100}%` }}
              />
            )}
            {falhas > 0 && (
              <div 
                className="bg-red-500 h-full"
                style={{ width: `${(falhas / totalMensagens) * 100}%` }}
              />
            )}
            {(totalMensagens - lidas - respondidas - falhas) > 0 && (
              <div 
                className="bg-gray-300 h-full"
                style={{ width: `${((totalMensagens - lidas - respondidas - falhas) / totalMensagens) * 100}%` }}
              />
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {lidas > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-[9px] text-gray-500">Lidas ({lidas})</span>
              </div>
            )}
            {respondidas > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <span className="text-[9px] text-gray-500">Respondidas ({respondidas})</span>
              </div>
            )}
            {falhas > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                <span className="text-[9px] text-gray-500">Falhas ({falhas})</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// 🎯 EXPORTAÇÃO PADRÃO
// ============================================================================
export default StatsCards;